import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api, { API_BASE } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { CartItem, Address, ShippingOption, CouponValidation, Order, PayPalOrderResponse, PayPalCaptureResponse } from '../types';
import './Cart.css';

interface CheckoutFormProps {
  cartItems: CartItem[];
  subtotal: number;
  shippingCost: number;
  tax: number;
  discountAmount: number;
  total: number;
  shippingOptionId: number | null;
  onSuccess: () => void;
  isGuest: boolean;
  onTaxUpdate: (tax: number) => void;
  couponCode?: string;
}

interface ShippingInfo {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  shippingOptionId: number | null;
  guestEmail: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  cartItems,
  subtotal,
  shippingCost,
  tax,
  discountAmount,
  total,
  shippingOptionId,
  onSuccess,
  isGuest,
  onTaxUpdate,
  couponCode
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { success, error: showError } = useToast();
  const [processing, setProcessing] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [savedAddresses, setSavedAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [useSavedAddress, setUseSavedAddress] = useState<boolean>(false);
  const [saveAddress, setSaveAddress] = useState<boolean>(false);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    shippingOptionId: shippingOptionId,
    guestEmail: '',
  });
  const [orderId, setOrderId] = useState<number | null>(null);

  const fetchSavedAddresses = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Address[]>('/addresses');
      setSavedAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch saved addresses:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isGuest) {
      fetchSavedAddresses();
    }
  }, [fetchSavedAddresses, isAuthenticated, isGuest]);

  useEffect(() => {
    if (useSavedAddress && selectedAddressId) {
      const address = savedAddresses.find(a => a.id === selectedAddressId);
      if (address) {
        setShippingInfo((prev) => ({
          ...prev,
          address: address.addressLine1,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          country: address.country || 'US',
        }));
      }
    }
  }, [selectedAddressId, useSavedAddress, savedAddresses]);

  const handleCreateOrder = async (e?: React.FormEvent): Promise<void> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zipCode) {
      showError('Please fill in all shipping information');
      return;
    }

    if (isGuest && !shippingInfo.guestEmail) {
      showError('Email is required for guest checkout');
      return;
    }

    if (!shippingInfo.shippingOptionId) {
      showError('Please select a shipping method');
      return;
    }

    setProcessing(true);
    try {
      const orderData = {
        shippingAddress: shippingInfo.address,
        shippingCity: shippingInfo.city,
        shippingState: shippingInfo.state,
        shippingZipCode: shippingInfo.zipCode,
        shippingCountry: shippingInfo.country,
        shippingOptionId: shippingInfo.shippingOptionId,
        guestEmail: isGuest ? shippingInfo.guestEmail : undefined,
        shippingAddressId: useSavedAddress && selectedAddressId ? selectedAddressId : undefined,
        couponCode: couponCode || undefined,
      };

      const orderResponse = await api.post<{ order: Order }>('/orders', orderData);
      const order = orderResponse.data.order;
      setOrderId(order.id);
      
      // Update tax from response
      if (onTaxUpdate && order.tax !== undefined) {
        onTaxUpdate(order.tax);
      }
      
      // Order intent created - will be confirmed after payment
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to create order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentMethod = async (method: string): Promise<void> => {
    if (method !== 'Square') return;
    
    let currentOrderId = orderId;
    
    if (!currentOrderId) {
      if (!shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zipCode) {
        showError('Please fill in all shipping information');
        return;
      }

      if (isGuest && !shippingInfo.guestEmail) {
        showError('Email is required for guest checkout');
        return;
      }

      setProcessing(true);
      try {
        const orderData = {
          shippingAddress: shippingInfo.address,
          shippingCity: shippingInfo.city,
          shippingState: shippingInfo.state,
          shippingZipCode: shippingInfo.zipCode,
          shippingCountry: shippingInfo.country,
          shippingOptionId: shippingInfo.shippingOptionId,
          guestEmail: isGuest ? shippingInfo.guestEmail : undefined,
          shippingAddressId: useSavedAddress && selectedAddressId ? selectedAddressId : undefined,
          couponCode: couponCode || undefined,
        };
        const orderResponse = await api.post<{ order: Order }>('/orders', orderData);
        currentOrderId = orderResponse.data.order.id;
        setOrderId(currentOrderId);
        // Don't show toast - order is only truly created after payment
      } catch (err: any) {
        showError(err.response?.data?.message || 'Failed to create order. Please try again.');
        setProcessing(false);
        return;
      }
    }

    setProcessing(true);
    setSelectedPaymentMethod(method);
    try {
      const response = await api.post<{ paymentUrl: string }>('/payment/create-link', {
        orderId: currentOrderId,
        paymentMethod: method
      });

      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to create payment link. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayPalCreateOrder = async (): Promise<string> => {
    let currentOrderId = orderId;
    
    if (!currentOrderId) {
      if (!shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zipCode) {
        throw new Error('Please fill in all shipping information');
      }

      if (isGuest && !shippingInfo.guestEmail) {
        throw new Error('Email is required for guest checkout');
      }

      setProcessing(true);
      try {
        const orderData = {
          shippingAddress: shippingInfo.address,
          shippingCity: shippingInfo.city,
          shippingState: shippingInfo.state,
          shippingZipCode: shippingInfo.zipCode,
          shippingCountry: shippingInfo.country,
          shippingOptionId: shippingInfo.shippingOptionId,
          guestEmail: isGuest ? shippingInfo.guestEmail : undefined,
          shippingAddressId: useSavedAddress && selectedAddressId ? selectedAddressId : undefined,
          couponCode: couponCode || undefined,
        };
        const orderResponse = await api.post<{ order: Order }>('/orders', orderData);
        currentOrderId = orderResponse.data.order.id;
        setOrderId(currentOrderId);
      } catch (err: any) {
        setProcessing(false);
        throw new Error(err.response?.data?.message || 'Failed to create order');
      }
    }

    try {
      const response = await api.post<PayPalOrderResponse>('/payment/paypal/orders', {
        orderId: currentOrderId
      });

      if (response.data.id) {
        return response.data.id;
      } else {
        const errorDetail = response.data?.details?.[0];
        const errorMessage = errorDetail
          ? `${errorDetail.issue} ${errorDetail.description}`
          : JSON.stringify(response.data);
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      setProcessing(false);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create PayPal order';
      showError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handlePayPalApprove = async (data: any, actions: any): Promise<void> => {
    try {
      const response = await api.post<PayPalCaptureResponse>(`/payment/paypal/orders/${data.orderID}/capture`, {
        orderId: orderId
      });

      const errorDetail = response.data?.details?.[0];

      if (errorDetail?.issue === 'INSTRUMENT_DECLINED') {
        return actions.restart();
      } else if (errorDetail) {
        throw new Error(`${errorDetail.description}`);
      } else {
        const transaction = response.data.purchase_units?.[0]?.payments?.captures?.[0];
        success(`Payment successful! Transaction ID: ${transaction?.id || data.orderID}`);
        
        setTimeout(() => {
          navigate('/payment/success?orderId=' + orderId);
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Payment could not be processed';
      showError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  return (
    <div className="checkout-form">
      <h3>Shipping Information</h3>
      
      {isAuthenticated && !isGuest && savedAddresses.length > 0 && (
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={useSavedAddress}
              onChange={(e) => setUseSavedAddress(e.target.checked)}
              disabled={processing && !!orderId}
            />
            Use saved address
          </label>
          {useSavedAddress && (
            <select
              value={selectedAddressId || ''}
              onChange={(e) => setSelectedAddressId(parseInt(e.target.value) || null)}
              disabled={processing && !!orderId}
              style={{ marginTop: '10px', width: '100%', padding: '8px' }}
            >
              <option value="">Select an address</option>
              {savedAddresses.map((addr) => (
                <option key={addr.id} value={addr.id}>
                  {addr.addressLine1}, {addr.city}, {addr.state} {addr.zipCode}
                  {addr.isDefault && ' (Default)'}
                </option>
              ))}
            </select>
          )}
          {!useSavedAddress && (
            <Link to="/addresses" style={{ marginLeft: '10px', fontSize: '14px' }}>
              Manage addresses
            </Link>
          )}
        </div>
      )}

      {(!useSavedAddress || !selectedAddressId) && (
        <>
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={shippingInfo.address}
              onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
              required
              disabled={processing && !!orderId}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={shippingInfo.city}
                onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                required
                disabled={processing && !!orderId}
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={shippingInfo.state}
                onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                required
                disabled={processing && !!orderId}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={shippingInfo.zipCode}
                onChange={(e) => {
                  // Allow only digits and hyphens, limit length
                  const value = e.target.value.replace(/[^0-9-]/g, '').slice(0, 10);
                  setShippingInfo({ ...shippingInfo, zipCode: value });
                }}
                onBlur={(e) => {
                  // Validate ZIP code format (US: 5 digits or 5+4 format)
                  const zip = e.target.value.trim();
                  if (zip && !/^\d{5}(-\d{4})?$/.test(zip)) {
                    showError('Please enter a valid ZIP code (e.g., 12345 or 12345-6789)');
                  }
                }}
                pattern="^\d{5}(-\d{4})?$"
                placeholder="12345 or 12345-6789"
                required
                disabled={processing && !!orderId}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={shippingInfo.country}
                onChange={(e) => setShippingInfo({ ...shippingInfo, country: e.target.value })}
                required
                disabled={processing && !!orderId}
              />
            </div>
          </div>
          {isAuthenticated && !isGuest && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={saveAddress}
                  onChange={(e) => setSaveAddress(e.target.checked)}
                  disabled={processing && !!orderId}
                />
                Save this address for future orders
              </label>
            </div>
          )}
        </>
      )}

      {isGuest && (
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={shippingInfo.guestEmail}
            onChange={(e) => setShippingInfo({ ...shippingInfo, guestEmail: e.target.value })}
            required
            disabled={processing && !!orderId}
          />
        </div>
      )}

      {!orderId ? (
        <div className="checkout-actions">
          <button
            type="button"
            onClick={() => handleCreateOrder()}
            disabled={processing}
            className="btn btn-primary btn-block"
          >
            {processing ? 'Creating Order...' : 'Continue to Payment'}
          </button>
        </div>
      ) : (
        <>
          <h3>Select Payment Method</h3>
          <div className="payment-methods">
            <div className="payment-method-option">
              <button
                type="button"
                onClick={() => handlePaymentMethod('Square')}
                disabled={processing}
                className="payment-button square-button"
              >
                <span className="square-logo" aria-hidden="true">
                  <svg viewBox="0 0 994.6 994.6" role="img" aria-label="Square">
                    <path
                      fill="currentColor"
                      d="M828.4,0H166.2C74.4,0,0,74.4,0,166.2v662.2c0,91.8,74.4,166.2,166.2,166.2h662.2
    c91.8,0,166.2-74.4,166.2-166.2V166.2C994.6,74.4,920.2,0,828.4,0z M813.8,761.3c0,29-23.5,52.5-52.5,52.5h-528
    c-29,0-52.5-23.5-52.5-52.5v-528c0-29,23.5-52.5,52.5-52.5h528c29,0,52.5,23.5,52.5,52.5V761.3z M391.8,632.3
    c-16.7,0-30.1-13.5-30.1-30.2V391.3c0-16.7,13.4-30.3,30.1-30.3h211.1c16.6,0,30.1,13.5,30.1,30.3V602c0,16.7-13.5,30.2-30.1,30.2
    H391.8z"
                    />
                  </svg>
                </span>
                <span>Pay with Square</span>
              </button>
            </div>
            <div className="payment-method-option">
              <PayPalScriptProvider
                options={{
                  clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "ASod2aStS0FBdMDPL5eJiaWwachmzxzpHTY7WkLA_8lxFgLljQIR4f8I0cItwM4_roXtqz5Z_IRK0NpS",
                  currency: "USD",
                  intent: "capture",
                }}
              >
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "paypal",
                  }}
                  createOrder={handlePayPalCreateOrder}
                  onApprove={handlePayPalApprove}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    showError('PayPal payment error. Please try again.');
                  }}
                  onCancel={(data) => {
                    console.log('PayPal cancelled:', data);
                    showError('Payment was cancelled.');
                  }}
                />
              </PayPalScriptProvider>
            </div>
          </div>
          {processing && selectedPaymentMethod === 'Square' && (
            <div className="processing-message">
              <p>Redirecting to payment provider...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Cart: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { success, error: showError } = useToast();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShippingOption, setSelectedShippingOption] = useState<number | null>(null);
  const [shippingCost, setShippingCost] = useState<number>(0);
  const [tax, setTax] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponError, setCouponError] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCheckout, setShowCheckout] = useState<boolean>(false);
  const navigate = useNavigate();

  const fetchCart = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<CartItem[]>('/cart');
      setCartItems(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchShippingOptions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<ShippingOption[]>('/shipping/options');
      setShippingOptions(response.data);
      if (response.data.length > 0) {
        setSelectedShippingOption(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch shipping options:', error);
    }
  }, []);

  const calculateShipping = useCallback(async (): Promise<void> => {
    if (!selectedShippingOption) return;

    const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    try {
      const response = await api.post<{ cost: number }>('/shipping/calculate', {
        shippingOptionId: selectedShippingOption,
        itemCount,
      });
      setShippingCost(response.data.cost);
    } catch (error) {
      console.error('Failed to calculate shipping:', error);
    }
  }, [cartItems, selectedShippingOption]);

  useEffect(() => {
    fetchCart();
    fetchShippingOptions();
  }, [fetchCart, fetchShippingOptions]);

  useEffect(() => {
    if (selectedShippingOption && cartItems.length > 0) {
      calculateShipping();
    }
  }, [calculateShipping, cartItems.length, selectedShippingOption]);

  const applyCoupon = async (): Promise<void> => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    const currentSubtotal = cartItems.reduce((sum, item) => {
      const basePrice = item.productVariant?.price || item.product.price;
      const salePrice = (item.product as any).salePrice;
      const price = salePrice && salePrice < basePrice ? salePrice : basePrice;
      return sum + price * item.quantity;
    }, 0);

    try {
      const response = await api.post<CouponValidation>('/coupons/validate', {
        code: couponCode.trim(),
        subtotal: currentSubtotal
      });

      if (response.data.isValid) {
        setDiscountAmount(response.data.discountAmount || 0);
        setAppliedCoupon(response.data);
        setCouponError('');
        success('Coupon applied successfully!');
      } else {
        setCouponError(response.data.errorMessage || 'Invalid coupon code');
        setDiscountAmount(0);
        setAppliedCoupon(null);
      }
    } catch (error: any) {
      setCouponError(error.response?.data?.message || 'Failed to validate coupon');
      setDiscountAmount(0);
      setAppliedCoupon(null);
    }
  };

  const removeCoupon = (): void => {
    setCouponCode('');
    setDiscountAmount(0);
    setAppliedCoupon(null);
    setCouponError('');
  };

  const updateQuantity = async (id: number, quantity: number): Promise<void> => {
    if (quantity <= 0) {
      await removeItem(id);
      return;
    }

    try {
      await api.put(`/cart/${id}`, { quantity });
      fetchCart();
      // Re-apply coupon if one was applied
      if (appliedCoupon) {
        applyCoupon();
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      showError('Failed to update quantity');
    }
  };

  const removeItem = async (id: number): Promise<void> => {
    try {
      await api.delete(`/cart/${id}`);
      fetchCart();
      // Re-apply coupon if one was applied
      if (appliedCoupon) {
        applyCoupon();
      }
    } catch (error) {
      console.error('Failed to remove item:', error);
      showError('Failed to remove item');
    }
  };

  const handleCheckoutSuccess = (): void => {
    // This will be handled by the payment redirect
  };

  const handleTaxUpdate = (newTax: number): void => {
    setTax(newTax);
  };

  const subtotal = cartItems.reduce((sum, item) => {
    const basePrice = item.productVariant?.price || item.product.price;
    const salePrice = (item.product as any).salePrice;
    const price = salePrice && salePrice < basePrice ? salePrice : basePrice;
    return sum + price * item.quantity;
  }, 0);
  
  const total = subtotal - discountAmount + shippingCost + tax;

  if (loading) {
    return <div className="loading">Loading cart...</div>;
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="page-title">Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <p>Your cart is empty</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items">
              {cartItems.map((item) => {
                const basePrice = item.productVariant?.price || item.product.price;
                // Check if product has sale information (from artwork)
                const salePrice = (item.product as any).salePrice;
                const originalPrice = (item.product as any).originalPrice;
                const activeSale = (item.product as any).activeSale;
                const price = salePrice && salePrice < basePrice ? salePrice : basePrice;
                const displayPrice = price * item.quantity;
                const originalDisplayPrice = originalPrice && originalPrice > price ? originalPrice * item.quantity : null;
                const variantOptions = item.productVariant?.options || {};

                return (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-image">
                      {(() => {
                        let imageUrl = item.product.isCustomPrint 
                          ? (item.product.croppedImageUrl || item.product.imageUrl)
                          : (item.product.croppedImageUrl || item.product.imageUrl);
                        
                        if (imageUrl) {
                          if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
                            const fallback = item.product.croppedImageUrl === imageUrl 
                              ? item.product.imageUrl 
                              : item.product.croppedImageUrl;
                            
                            if (fallback && !fallback.startsWith('blob:') && !fallback.startsWith('data:')) {
                              imageUrl = fallback;
                            } else {
                              imageUrl = '/placeholder-image.jpg';
                            }
                          }
                          
                          if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:') && !imageUrl.startsWith('/placeholder')) {
                            imageUrl = `${API_BASE}${imageUrl}`;
                          }
                        } else {
                          imageUrl = '/placeholder-image.jpg';
                        }
                        
                        let aspectRatioStyle: React.CSSProperties = {};
                        if (item.product.isCustomPrint && item.product.aspectRatio) {
                          const aspectRatio = parseFloat(item.product.aspectRatio);
                          if (!isNaN(aspectRatio) && aspectRatio > 0) {
                            const maxHeight = 150;
                            const calculatedWidth = maxHeight * aspectRatio;
                            aspectRatioStyle = {
                              maxHeight: `${maxHeight}px`,
                              width: `${Math.min(calculatedWidth, 200)}px`,
                              height: 'auto',
                              aspectRatio: aspectRatio.toString()
                            };
                          }
                        }
                        
                        return (
                          <img
                            src={imageUrl}
                            alt={item.product.name}
                            style={aspectRatioStyle}
                            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                              const target = e.target as HTMLImageElement;
                              const fallbackUrl = item.product.imageUrl;
                              if (fallbackUrl && !fallbackUrl.startsWith('data:') && !fallbackUrl.startsWith('blob:') && target.src !== fallbackUrl) {
                                const fullFallbackUrl = fallbackUrl.startsWith('http')
                                  ? fallbackUrl
                                  : `${API_BASE}${fallbackUrl}`;
                                target.src = fullFallbackUrl;
                              } else {
                                target.src = '/placeholder-image.jpg';
                              }
                            }}
                          />
                        );
                      })()}
                    </div>
                    <div className="cart-item-details">
                      <h3>{item.product.name}</h3>
                      {item.product.sku && (
                        <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>SKU: {item.product.sku}</p>
                      )}
                      {item.productVariant?.sku && (
                        <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Variant SKU: {item.productVariant.sku}</p>
                      )}
                      {Object.keys(variantOptions).length > 0 && (
                        <div style={{ marginTop: '5px' }}>
                          {Object.entries(variantOptions).map(([key, value]) => (
                            <p key={key} style={{ fontSize: '14px', margin: '2px 0' }}>
                              {key}: {value}
                            </p>
                          ))}
                        </div>
                      )}
                      {!item.productVariant && (
                        <>
                          {item.product.size && <p>Size: {item.product.size}</p>}
                          {item.product.material && <p>Material: {item.product.material}</p>}
                          {item.product.finish && <p>Finish: {item.product.finish}</p>}
                        </>
                      )}
                      <div className="cart-item-actions">
                        <div className="quantity-controls">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                          <span>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                        </div>
                        <div className="cart-item-price">
                          {originalDisplayPrice && (
                            <span className="original-price" style={{ textDecoration: 'line-through', color: 'var(--text-secondary)', marginRight: '8px', fontSize: '0.9em' }}>
                              ${originalDisplayPrice.toFixed(2)}
                            </span>
                          )}
                          <span className={originalDisplayPrice ? 'sale-price' : ''}>
                            ${displayPrice.toFixed(2)}
                          </span>
                          {activeSale && (
                            <span className="sale-badge-inline" style={{ 
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: activeSale.badgeBackgroundColor || '#ff4444',
                              color: activeSale.badgeColor || '#ffffff',
                              borderRadius: '3px',
                              fontSize: '0.75em',
                              fontWeight: 700
                            }}>
                              {activeSale.badgeText || 'SALE'}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="btn btn-danger btn-small"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="cart-summary">
              <div className="summary-card">
                <h3>Order Summary</h3>
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                
                <div className="form-group" style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <label>Coupon Code</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponError('');
                      }}
                      placeholder="Enter coupon code"
                      disabled={!!appliedCoupon}
                      style={{ flex: 1, padding: '8px' }}
                    />
                    {appliedCoupon ? (
                      <button
                        onClick={removeCoupon}
                        className="btn btn-secondary"
                        style={{ padding: '8px 15px' }}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={applyCoupon}
                        className="btn btn-secondary"
                        style={{ padding: '8px 15px' }}
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  {couponError && (
                    <p style={{ color: 'var(--color-danger)', fontSize: '12px', marginTop: '5px' }}>{couponError}</p>
                  )}
                  {appliedCoupon && (
                    <p style={{ color: 'var(--color-success)', fontSize: '12px', marginTop: '5px' }}>
                      Coupon applied: {appliedCoupon.code}
                    </p>
                  )}
                </div>

                {discountAmount > 0 && (
                  <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                    <span>Discount:</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {!showCheckout && (
                  <div className="form-group">
                    <label>Shipping Method</label>
                    <select
                      value={selectedShippingOption || ''}
                      onChange={(e) => {
                        setSelectedShippingOption(parseInt(e.target.value) || null);
                      }}
                    >
                      {shippingOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name} ({option.carrier}) - ${option.basePrice.toFixed(2)} + ${option.pricePerItem.toFixed(2)} per item ({option.estimatedDays} days)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>${shippingCost.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Tax:</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>

                {!showCheckout ? (
                  <button
                    onClick={() => {
                      if (!selectedShippingOption) {
                        showError('Please select a shipping method');
                        return;
                      }
                      setShowCheckout(true);
                    }}
                    className="btn btn-primary btn-block"
                  >
                    Proceed to Checkout
                  </button>
                ) : (
                  <CheckoutForm
                    cartItems={cartItems}
                    subtotal={subtotal}
                    shippingCost={shippingCost}
                    tax={tax}
                    discountAmount={discountAmount}
                    total={total}
                    shippingOptionId={selectedShippingOption}
                    onSuccess={handleCheckoutSuccess}
                    isGuest={!isAuthenticated}
                    onTaxUpdate={handleTaxUpdate}
                    couponCode={appliedCoupon?.code}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;

